import { z } from 'zod';
// Zod schemas for validation
/**
 * Schema for validating user roles
 */
export const UserRoleSchema = z.enum(['admin', 'senior_manager', 'manager', 'contributor']);
/**
 * Schema for validating UserPivotData
 */
export const UserPivotDataSchema = z.object({
    id: z.number(),
    name: z.string(),
    email: z.string().email(),
    team_id: z.number(),
    team_name: z.string(),
    role_id: z.number(),
    role_name: z.string(),
    manager_level: z.number(),
    is_admin: z.boolean(),
    m1_id: z.number().optional(),
    m2_id: z.number().optional(),
    manager_user_id: z.number().optional(),
    status: z.string(),
    hire_date: z.date(),
});
/**
 * Schema for validating app permissions
 */
export const AppPermissionsSchema = z.object({
    canSetup: z.boolean(),
    canReview: z.boolean(),
    canAdmin: z.boolean(),
    canCollaborate: z.boolean(),
    canViewSensitiveFinanceData: z.boolean().default(false),
});
/**
 * Schema for validating AppUser
 */
export const AppUserSchema = z.object({
    uid: z.string().min(1),
    employee_id: z.number(),
    app_permissions: AppPermissionsSchema,
    assigned_workflows: z.array(z.number()),
    last_active: z.date(),
    created_at: z.date(),
});
/**
 * Schema for validating complete hybrid user data
 */
export const UserSchema = z.object({
    uid: z.string().min(1),
    employee_id: z.number(),
    pivot_data: UserPivotDataSchema,
    app_data: AppUserSchema,
    role: UserRoleSchema,
    permissions: AppPermissionsSchema,
});
/**
 * Schema for validating user creation data
 */
export const CreateUserDataSchema = z.object({
    employee_id: z.number(),
    uid: z.string().min(1),
    app_permissions: AppPermissionsSchema.partial().optional(),
    assigned_workflows: z.array(z.number()).optional().default([]),
});
/**
 * Schema for validating user update data
 */
export const UpdateUserDataSchema = z.object({
    app_permissions: AppPermissionsSchema.partial().optional(),
    assigned_workflows: z.array(z.number()).optional(),
});
/**
 * Schema for validating user profile data with cached avatar support
 */
export const UserProfileSchema = z.object({
    uid: z.string().min(1),
    employee_id: z.number(),
    email: z.string().email(),
    name: z.string().min(1),
    team_name: z.string(),
    role_name: z.string(),
    role: UserRoleSchema,
    lastActive: z.date(),
    // Avatar fields - backward compatible
    avatar: z.string().url().optional(),
    // New cached avatar fields
    cached_avatar_url: z.string().url().optional(),
    cached_thumbnail_url: z.string().url().optional(),
    avatar_cache_timestamp: z.date().optional(),
    avatar_cache_status: z.enum(['pending', 'cached', 'failed']).optional(),
});
/**
 * Type guards for user data
 */
export const isValidUser = (data) => {
    return UserSchema.safeParse(data).success;
};
export const isValidUserRole = (role) => {
    return UserRoleSchema.safeParse(role).success;
};
export const isValidAppPermissions = (permissions) => {
    return AppPermissionsSchema.safeParse(permissions).success;
};
export const isValidUserPivotData = (data) => {
    return UserPivotDataSchema.safeParse(data).success;
};
export const isValidAppUser = (data) => {
    return AppUserSchema.safeParse(data).success;
};
